import React, { useState } from 'react';
import { Mail, Lock, Eye, EyeOff, ArrowRight, Sparkles, User, ArrowLeft, CheckCircle } from 'lucide-react';
import { sendPasswordResetEmail } from 'firebase/auth';
import { auth } from '../../firebase';
import boplyLogo from '../../assets/boply-logo.png';

const LoginForm = ({ onLogin, onSignup, authMode, setAuthMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Forgot password state
  const [view, setView] = useState('auth'); // 'auth' | 'forgot' | 'reset-sent'
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (authMode === 'signup' && !displayName.trim()) {
      setError('Please enter a display name');
      return;
    }

    setIsLoading(true);
    try {
      if (authMode === 'login') {
        await onLogin(email, password);
      } else {
        await onSignup(email, password, displayName.trim());
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setResetError('');
    setResetLoading(true);

    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setView('reset-sent');
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setResetError('No account found with that email address.');
      } else if (err.code === 'auth/invalid-email') {
        setResetError('Please enter a valid email address.');
      } else {
        setResetError('Something went wrong. Please try again.');
      }
    } finally {
      setResetLoading(false);
    }
  };

  const handleBackToLogin = () => {
    setView('auth');
    setResetEmail('');
    setResetError('');
  };

  // Shared background
  const Background = () => (
    <div className="absolute inset-0 overflow-hidden">
      <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" />
      <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" style={{ animationDelay: '1s' }} />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-indigo-500 rounded-full mix-blend-multiply filter blur-3xl opacity-10 animate-pulse" style={{ animationDelay: '2s' }} />
    </div>
  );

  // =====================================================
  // FORGOT PASSWORD VIEW
  // =====================================================
  if (view === 'forgot') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
        <Background />
        <div className="relative w-full max-w-md">
          <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-blue-500 to-purple-600 rounded-2xl blur-lg opacity-30" />
          <div className="relative bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-8">

            {/* Back button */}
            <button
              onClick={handleBackToLogin}
              className="flex items-center gap-2 text-purple-300/70 hover:text-purple-200 transition-colors mb-6 text-sm"
            >
              <ArrowLeft size={16} />
              Back to login
            </button>

            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-500/20 rounded-2xl mb-4 border border-purple-500/30">
                <Mail size={28} className="text-purple-300" />
              </div>
              <h2 className="text-2xl font-bold text-white mb-2">Reset Password</h2>
              <p className="text-purple-200/60 text-sm">
                Enter your email and we'll send you a link to reset your password.
              </p>
            </div>

            <form onSubmit={handleForgotPassword} className="space-y-5">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-purple-200/90">
                  Email Address
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <Mail size={18} className="text-purple-300/50 group-focus-within:text-purple-400 transition-colors" />
                  </div>
                  <input
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="your@email.com"
                    required
                    autoFocus
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-purple-300/40 focus:outline-none focus:border-purple-400/50 focus:bg-white/10 focus:ring-2 focus:ring-purple-400/20 transition-all"
                  />
                </div>
              </div>

              {resetError && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-300 px-4 py-3 rounded-xl text-sm">
                  {resetError}
                </div>
              )}

              <button
                type="submit"
                disabled={resetLoading}
                className="relative w-full group"
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl blur opacity-60 group-hover:opacity-100 transition duration-300" />
                <div className="relative w-full bg-gradient-to-r from-purple-500 to-blue-500 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-purple-500/25 transition-all disabled:opacity-70">
                  {resetLoading ? (
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      Send Reset Link
                      <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                    </>
                  )}
                </div>
              </button>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // =====================================================
  // RESET EMAIL SENT VIEW
  // =====================================================
  if (view === 'reset-sent') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
        <Background />
        <div className="relative w-full max-w-md">
          <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-blue-500 to-purple-600 rounded-2xl blur-lg opacity-30" />
          <div className="relative bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-8 text-center">

            <div className="inline-flex items-center justify-center w-20 h-20 bg-green-500/20 rounded-full mb-6 border border-green-500/30">
              <CheckCircle size={40} className="text-green-400" />
            </div>

            <h2 className="text-2xl font-bold text-white mb-3">Check Your Email</h2>
            <p className="text-purple-200/60 text-sm mb-2">
              We sent a password reset link to:
            </p>
            <p className="text-purple-300 font-semibold mb-6">{resetEmail}</p>
            <p className="text-purple-200/50 text-xs mb-8">
              Didn't get it? Check your spam folder or try again.
            </p>

            <div className="space-y-3">
              <button
                onClick={handleBackToLogin}
                className="relative w-full group"
              >
                <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl blur opacity-60 group-hover:opacity-100 transition duration-300" />
                <div className="relative w-full bg-gradient-to-r from-purple-500 to-blue-500 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 transition-all">
                  Back to Login
                  <ArrowRight size={18} />
                </div>
              </button>

              <button
                onClick={() => { setView('forgot'); setResetError(''); }}
                className="w-full py-3 text-purple-300/60 hover:text-purple-300 text-sm transition-colors"
              >
                Try a different email
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =====================================================
  // MAIN AUTH VIEW (login / signup)
  // =====================================================
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4 relative overflow-hidden">
      <Background />

      <div className="relative w-full max-w-md">
        <div className="absolute -inset-1 bg-gradient-to-r from-purple-600 via-blue-500 to-purple-600 rounded-2xl blur-lg opacity-30" />
        
        <div className="relative bg-white/10 backdrop-blur-xl rounded-2xl border border-white/20 shadow-2xl p-8">
          {/* Logo and title */}
          <div className="text-center mb-8">
            <div className="inline-flex items-center justify-center mb-4">
              <img src={boplyLogo} alt="Boply" className="w-20 h-20 rounded-2xl" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-1 tracking-tight">Boply</h1>
            <p className="text-purple-200/80 text-sm flex items-center justify-center gap-1">
              <Sparkles size={14} />
              Band & Gig Management
            </p>
          </div>

          {/* Toggle tabs */}
          <div className="relative flex bg-white/5 rounded-xl p-1 mb-8">
            <div 
              className={`absolute top-1 bottom-1 w-[calc(50%-4px)] bg-gradient-to-r from-purple-500 to-blue-500 rounded-lg transition-all duration-300 ease-out shadow-lg ${
                authMode === 'signup' ? 'left-[calc(50%+2px)]' : 'left-1'
              }`}
            />
            <button
              onClick={() => { setAuthMode('login'); setError(''); setDisplayName(''); }}
              className={`relative flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors duration-300 ${
                authMode === 'login' ? 'text-white' : 'text-purple-200/70 hover:text-white'
              }`}
            >
              Log In
            </button>
            <button
              onClick={() => { setAuthMode('signup'); setError(''); }}
              className={`relative flex-1 py-2.5 text-sm font-semibold rounded-lg transition-colors duration-300 ${
                authMode === 'signup' ? 'text-white' : 'text-purple-200/70 hover:text-white'
              }`}
            >
              Sign Up
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Display Name - signup only */}
            {authMode === 'signup' && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-purple-200/90">
                  Display Name
                </label>
                <div className="relative group">
                  <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                    <User size={18} className="text-purple-300/50 group-focus-within:text-purple-400 transition-colors" />
                  </div>
                  <input
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="What should we call you?"
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-purple-300/40 focus:outline-none focus:border-purple-400/50 focus:bg-white/10 focus:ring-2 focus:ring-purple-400/20 transition-all"
                  />
                </div>
                <p className="text-xs text-purple-300/50 pl-1">This is how you'll appear to bandmates</p>
              </div>
            )}

            {/* Email */}
            <div className="space-y-2">
              <label className="block text-sm font-medium text-purple-200/90">Email</label>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Mail size={18} className="text-purple-300/50 group-focus-within:text-purple-400 transition-colors" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  className="w-full pl-11 pr-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-purple-300/40 focus:outline-none focus:border-purple-400/50 focus:bg-white/10 focus:ring-2 focus:ring-purple-400/20 transition-all"
                />
              </div>
            </div>

            {/* Password */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-purple-200/90">Password</label>
                {/* Forgot password link — only on login */}
                {authMode === 'login' && (
                  <button
                    type="button"
                    onClick={() => { setView('forgot'); setResetEmail(email); setResetError(''); }}
                    className="text-xs text-purple-300/60 hover:text-purple-300 transition-colors"
                  >
                    Forgot password?
                  </button>
                )}
              </div>
              <div className="relative group">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                  <Lock size={18} className="text-purple-300/50 group-focus-within:text-purple-400 transition-colors" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="w-full pl-11 pr-12 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-purple-300/40 focus:outline-none focus:border-purple-400/50 focus:bg-white/10 focus:ring-2 focus:ring-purple-400/20 transition-all"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-purple-300/50 hover:text-purple-300 transition-colors"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {authMode === 'signup' && (
                <p className="text-xs text-purple-300/50 pl-1">Must be at least 6 characters</p>
              )}
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-500/10 border border-red-500/20 text-red-300 px-4 py-3 rounded-xl text-sm backdrop-blur-sm">
                {error}
              </div>
            )}

            {/* Submit */}
            <button type="submit" disabled={isLoading} className="relative w-full group">
              <div className="absolute -inset-0.5 bg-gradient-to-r from-purple-500 to-blue-500 rounded-xl blur opacity-60 group-hover:opacity-100 transition duration-300" />
              <div className="relative w-full bg-gradient-to-r from-purple-500 to-blue-500 text-white py-3.5 rounded-xl font-semibold flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-purple-500/25 transition-all disabled:opacity-70">
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                ) : (
                  <>
                    {authMode === 'login' ? 'Log In' : 'Create Account'}
                    <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </div>
            </button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center text-sm text-purple-200/60">
            {authMode === 'login' ? (
              <p>
                Don't have an account?{' '}
                <button
                  onClick={() => { setAuthMode('signup'); setError(''); }}
                  className="text-purple-300 font-semibold hover:text-white transition-colors"
                >
                  Sign up
                </button>
              </p>
            ) : (
              <p>
                Already have an account?{' '}
                <button
                  onClick={() => { setAuthMode('login'); setError(''); setDisplayName(''); }}
                  className="text-purple-300 font-semibold hover:text-white transition-colors"
                >
                  Log in
                </button>
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginForm;